/**
 * LydSlidere — DOM-baserte range-slidere for musikk/sfx/ambient.
 * Erstatter +/-knapp-mønsteret fra runde 41 i Direksjon → Lyd-seksjon.
 *
 * Bygd i runde 42 etter persona-test som etterspurte mer naturlig
 * finkontroll på lyd.
 *
 * Strategi:
 * - DOM-elementene mountes over Phaser-canvas i `#sp-canvas-host` og
 *   posisjoneres absolutt der Phaser-renderingen tidligere viste rad-
 *   kortene. DireksjonDashboard kaller `mount(boks)` med pixel-bounds.
 * - ResizeObserver på canvas-host holder slidernes posisjon i takt
 *   med scale.
 * - Verdier leses fra/skrives til `lydInnstillinger`. Når andre kilder
 *   endrer verdien (f.eks. localStorage), subscriber vi for å holde
 *   slider-posisjonen synkronisert.
 */
import { lydInnstillinger } from '@/systems/lydInnstillinger';
import { voiceOver } from '@/systems/voiceOver';

const ROT_ID = 'sp-lyd-slidere';
const STYLE_ID = 'sp-lyd-slidere-style';

const CSS = `
#${ROT_ID} {
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: auto;
  font-family: var(--sp-font-hud, 'Inter Tight', system-ui, sans-serif);
  z-index: 9;
}
#${ROT_ID} .sp-lyd-rad {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
  background: rgba(255,255,255,0.96);
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(139,94,60,0.4);
}
#${ROT_ID} .sp-lyd-label {
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
  grid-column: 1 / 2;
}
#${ROT_ID} .sp-lyd-verdi {
  font-weight: 700;
  font-size: 14px;
  color: #862736;
  grid-column: 2 / 3;
  min-width: 44px;
  text-align: right;
}
#${ROT_ID} input[type="range"] {
  grid-column: 1 / 3;
  width: 100%;
  accent-color: #862736;
  height: 28px;
  cursor: pointer;
}
#${ROT_ID} input[type="range"]:focus-visible {
  outline: 3px solid #862736;
  outline-offset: 4px;
}
/* Runde 45: voice-over-toggle som 4. rad */
#${ROT_ID} .sp-vo-rad {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
  background: rgba(255,255,255,0.96);
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(139,94,60,0.4);
}
#${ROT_ID} .sp-vo-label {
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
}
#${ROT_ID} .sp-vo-toggle {
  font-weight: 700;
  font-size: 14px;
  color: #ffffff;
  background: #862736;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  min-width: 64px;
  min-height: 44px;
}
#${ROT_ID} .sp-vo-toggle[aria-pressed="false"] {
  background: #9a9285;
}
#${ROT_ID} .sp-vo-toggle:focus-visible {
  outline: 3px solid #1a1a1a;
  outline-offset: 3px;
}
`;

type Kategori = 'musikk' | 'sfx' | 'ambient';

interface RadKonfig {
  kategori: Kategori;
  label: string;
}

const RADER: ReadonlyArray<RadKonfig> = [
  { kategori: 'musikk', label: 'Musikk' },
  { kategori: 'sfx', label: 'Lydeffekter' },
  { kategori: 'ambient', label: 'Romlyd (ambient)' },
];

export interface LydSliderBounds {
  /** Pikselbreddde i Phaser-canvas-koordinater (0..GAME_WIDTH). */
  width: number;
  /** Y-posisjon i Phaser-koordinater. */
  y: number;
  /** X-offset i Phaser-koordinater. */
  x: number;
}

export class LydSlidere {
  private rot?: HTMLDivElement;
  private innput: Map<Kategori, HTMLInputElement> = new Map();
  private verdiSpan: Map<Kategori, HTMLSpanElement> = new Map();
  private unsubLyd?: () => void;
  private unsubVO?: () => void;
  private voToggle?: HTMLButtonElement;
  private resizeObserver?: ResizeObserver;
  private bounds?: LydSliderBounds;

  mount(bounds: LydSliderBounds): void {
    if (typeof document === 'undefined') return;
    this.bounds = bounds;

    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = CSS;
      document.head.appendChild(style);
    }

    const host = document.getElementById('sp-canvas-host') ?? document.body;

    const eksisterende = document.getElementById(ROT_ID);
    if (eksisterende?.parentNode) {
      eksisterende.parentNode.removeChild(eksisterende);
    }

    const rot = document.createElement('div');
    rot.id = ROT_ID;
    rot.setAttribute('role', 'group');
    rot.setAttribute('aria-label', 'Lydinnstillinger');
    host.appendChild(rot);
    this.rot = rot;

    this.innput.clear();
    this.verdiSpan.clear();

    for (const rad of RADER) {
      const radEl = document.createElement('div');
      radEl.className = 'sp-lyd-rad';

      const labelEl = document.createElement('label');
      labelEl.className = 'sp-lyd-label';
      labelEl.textContent = rad.label;
      labelEl.htmlFor = `${ROT_ID}-${rad.kategori}`;

      const verdi = document.createElement('span');
      verdi.className = 'sp-lyd-verdi';
      verdi.textContent = `${this.lesVerdi(rad.kategori)}%`;
      verdi.setAttribute('aria-hidden', 'true');

      const input = document.createElement('input');
      input.type = 'range';
      input.id = `${ROT_ID}-${rad.kategori}`;
      input.min = '0';
      input.max = '100';
      input.step = '5';
      input.value = String(this.lesVerdi(rad.kategori));
      input.setAttribute('aria-label', `${rad.label} volum`);
      input.setAttribute('aria-valuemin', '0');
      input.setAttribute('aria-valuemax', '100');
      input.setAttribute('aria-valuenow', String(this.lesVerdi(rad.kategori)));

      input.addEventListener('input', () => {
        const nyVerdi = Number(input.value);
        this.skrivVerdi(rad.kategori, nyVerdi);
      });

      radEl.appendChild(labelEl);
      radEl.appendChild(verdi);
      radEl.appendChild(input);
      rot.appendChild(radEl);

      this.innput.set(rad.kategori, input);
      this.verdiSpan.set(rad.kategori, verdi);
    }

    // Runde 45: voice-over-toggle som 4. rad.
    this.byggVoiceOverRad(rot);

    this.unsubLyd = lydInnstillinger.subscribe(() => this.oppdaterFraStore());
    this.unsubVO = voiceOver.subscribe((aktiv) => this.oppdaterVoToggle(aktiv));
    this.posisjonerOverlay();
    this.startResizeObserver();
  }

  private byggVoiceOverRad(rot: HTMLDivElement): void {
    const rad = document.createElement('div');
    rad.className = 'sp-vo-rad';

    const label = document.createElement('label');
    label.className = 'sp-vo-label';
    label.textContent = 'Voice-over (Kristine, Haldis, Bjørn)';
    label.htmlFor = `${ROT_ID}-voice-over`;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.id = `${ROT_ID}-voice-over`;
    toggle.className = 'sp-vo-toggle';
    const aktiv = voiceOver.erAktiv() || (!voiceOver.erBlokkertEksternt && this.lesVoFraStorage());
    toggle.setAttribute('aria-pressed', String(aktiv));
    toggle.setAttribute('aria-label', 'Skru voice-over på eller av');
    toggle.textContent = aktiv ? 'PÅ' : 'AV';

    toggle.addEventListener('click', () => {
      const ny = voiceOver.toggle();
      this.oppdaterVoToggle(ny);
    });

    rad.appendChild(label);
    rad.appendChild(toggle);
    rot.appendChild(rad);
    this.voToggle = toggle;
  }

  private oppdaterVoToggle(aktiv: boolean): void {
    if (!this.voToggle) return;
    this.voToggle.setAttribute('aria-pressed', String(aktiv));
    this.voToggle.textContent = aktiv ? 'PÅ' : 'AV';
  }

  /** Heuristikk for første-render: voiceOver.erAktiv() krever Web Speech
   * API — i jsdom returnerer den false. For å vise toggle korrekt
   * sjekker vi localStorage direkte som backup. */
  private lesVoFraStorage(): boolean {
    if (typeof localStorage === 'undefined') return false;
    try {
      return localStorage.getItem('forlagshuset:voice-over-aktiv') === '1';
    } catch {
      return false;
    }
  }

  destroy(): void {
    this.unsubLyd?.();
    this.unsubLyd = undefined;
    this.unsubVO?.();
    this.unsubVO = undefined;
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    if (this.rot?.parentNode) this.rot.parentNode.removeChild(this.rot);
    this.rot = undefined;
    this.innput.clear();
    this.verdiSpan.clear();
    this.voToggle = undefined;
    this.bounds = undefined;
  }

  /** For tester: returnerer voice-over-toggle-knappen. */
  hentVoiceOverToggle(): HTMLButtonElement | undefined {
    return this.voToggle;
  }

  /** For tester: returnerer slider-input for en kategori. */
  hentInput(kategori: Kategori): HTMLInputElement | undefined {
    return this.innput.get(kategori);
  }

  get rotElement(): HTMLDivElement | undefined {
    return this.rot;
  }

  private lesVerdi(kategori: Kategori): number {
    const snap = lydInnstillinger.snapshot;
    if (kategori === 'musikk') return snap.musikkProsent;
    if (kategori === 'sfx') return snap.sfxProsent;
    return snap.ambientProsent;
  }

  private skrivVerdi(kategori: Kategori, prosent: number): void {
    if (kategori === 'musikk') lydInnstillinger.settMusikk(prosent);
    else if (kategori === 'sfx') lydInnstillinger.settSfx(prosent);
    else lydInnstillinger.settAmbient(prosent);
  }

  private oppdaterFraStore(): void {
    for (const [kategori, input] of this.innput) {
      const v = this.lesVerdi(kategori);
      if (Number(input.value) !== v) input.value = String(v);
      input.setAttribute('aria-valuenow', String(v));
      const verdi = this.verdiSpan.get(kategori);
      if (verdi) verdi.textContent = `${v}%`;
    }
  }

  private posisjonerOverlay(): void {
    if (!this.rot || !this.bounds) return;
    const host = document.getElementById('sp-canvas-host');
    if (!host) return;
    const hostW = host.clientWidth || 1;
    const hostH = host.clientHeight || 1;
    // Bounds er i Phaser-koordinater (1080×1920). Skaler til DOM-piksler.
    const scaleX = hostW / 1080;
    const scaleY = hostH / 1920;
    const left = Math.round(this.bounds.x * scaleX);
    const top = Math.round(this.bounds.y * scaleY);
    const width = Math.round(this.bounds.width * scaleX);
    this.rot.style.left = `${left}px`;
    this.rot.style.top = `${top}px`;
    this.rot.style.width = `${width}px`;
    void hostH;
  }

  private startResizeObserver(): void {
    const host = document.getElementById('sp-canvas-host');
    if (!host) return;
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => this.posisjonerOverlay());
    this.resizeObserver.observe(host);
  }
}

export const lydSlidere = new LydSlidere();
